import { AgentFeatureType, AgentVisibilityType } from "@/enums/ai";
import {
  validatePaginatedResponseWithSchema,
  validateWithSchema,
} from "@/helpers/common";
import { formatString } from "@/helpers/formatting";
import {
  aiAgentBaseSchema,
  aiAgentSchema,
  aiAgentSessionSchema,
} from "@/models/aiAgents";
import { axiosClient } from "@/services/client";
import endpoints from "@/services/endpoints";

import type {
  AiAgent,
  AiAgentBase,
  AiAgentSession,
  CreateAgentSessionPayload,
  CreateAiAgentPayload,
  PromptAgentPayload,
  UpdateAgentsVisibilityPayload,
  UpdateAiAgentPayload,
} from "@/models/aiAgents";
import type { IPaginatedResponse } from "@/models/common";

class AiAgentsApiService {
  async getAll(params?: Record<string, unknown>) {
    const { data } = await axiosClient.get<IPaginatedResponse<AiAgentBase>>(
      endpoints.AI_AGENTS.INDEX,
      { params }
    );

    return validatePaginatedResponseWithSchema(aiAgentBaseSchema, data);
  }

  async getById(id: AiAgent["id"]) {
    const { data } = await axiosClient.get<{ data: AiAgent }>(
      formatString(endpoints.AI_AGENTS.SINGLE, id)
    );

    return validateWithSchema(aiAgentSchema, data.data);
  }

  async createAIAgent(agent: CreateAiAgentPayload) {
    const { data } = await axiosClient.post<{ data: AiAgent }>(
      endpoints.AI_AGENTS.INDEX,
      agent
    );

    return validateWithSchema(aiAgentSchema, data.data);
  }

  async update(id: AiAgent["id"], payload: UpdateAiAgentPayload) {
    const { data } = await axiosClient.patch<{ data: AiAgent }>(
      formatString(endpoints.AI_AGENTS.SINGLE, id),
      payload
    );

    return validateWithSchema(aiAgentSchema, data.data);
  }

  async deleteById(id: AiAgent["id"]) {
    const { data } = await axiosClient.delete<{ data: { deleted: boolean } }>(
      formatString(endpoints.AI_AGENTS.SINGLE, id)
    );

    return data;
  }

  async createAgentSession(payload: CreateAgentSessionPayload) {
    const { data } = await axiosClient.post<{ data: AiAgentSession }>(
      endpoints.APPLICATIONS.AI.AGENTS.SESSIONS.CREATE,
      payload
    );

    return validateWithSchema(aiAgentSessionSchema, data.data);
  }

  async promptAgent({ session_id, content }: PromptAgentPayload) {
    await axiosClient.post<{ data: { session_id: string } }>(
      formatString(
        endpoints.APPLICATIONS.AI.AGENTS.SESSIONS.PROMPT,
        session_id
      ),
      { content }
    );
  }

  async getAgentSession(session_id: string) {
    const { data } = await axiosClient.get<{ data: AiAgentSession }>(
      formatString(endpoints.APPLICATIONS.AI.AGENTS.SESSIONS.SINGLE, session_id)
    );

    return validateWithSchema(aiAgentSessionSchema, data.data);
  }

  async getAllAgentSessions({ agent_id }: { agent_id: string }) {
    const { data } = await axiosClient.get<{ data: AiAgentSession[] }>(
      endpoints.APPLICATIONS.AI.AGENTS.SESSIONS.ALL,
      {
        params: {
          agent_id,
        },
      }
    );

    return validateWithSchema(aiAgentSessionSchema.array(), data.data);
  }

  async getAllAgentsSessions() {
    const { data } = await axiosClient.get<{ data: AiAgentSession[] }>(
      endpoints.APPLICATIONS.AI.AGENTS.SESSIONS.ALL
    );

    return validateWithSchema(aiAgentSessionSchema.array(), data.data);
  }

  async uploadFile(
    id: AiAgentSession["id"],
    file: File
  ): Promise<{ file_id: number }> {
    const formData = new FormData();
    formData.append("file", file);

    const { data } = await axiosClient.post<{ data: { id: number } }>(
      formatString(endpoints.APPLICATIONS.AI.AGENTS.SESSIONS.FILES, id),
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    return { file_id: data.data.id };
  }

  async updateAgentsVisibility({
    role,
    payload,
  }: {
    role: AgentVisibilityType;
    payload: UpdateAgentsVisibilityPayload;
  }) {
    const endpoint =
      role === AgentVisibilityType.User
        ? endpoints.AI_AGENTS.VISIBILITY.USER
        : endpoints.AI_AGENTS.VISIBILITY.CLIENT;
    const { data } = await axiosClient.put(endpoint, {
      ...payload,
      feature: AgentFeatureType.CommandBarAgentSearch,
    });

    return validateWithSchema(aiAgentBaseSchema.shape.visibilities, data.data);
  }
}

const aiAgentsApiService = new AiAgentsApiService();
export { aiAgentsApiService };
